import React from 'react';

import { useLocation } from 'react-router-dom';

import { containsLocale } from 'helpers/locales';

import { formMatchesLocation, klaviyoOpenForm } from './helpers';
import { useFormsQuery } from './useFormsQuery';
import useShownForms from './useShownForms';

export const useKlaviyoForms = () => {
  const formShown = React.useRef<boolean>(false);
  const { pathname, search } = useLocation();
  const { wasFormShown, markAsShown } = useShownForms();
  const { data } = useFormsQuery();

  const triggerPopup = React.useCallback(
    (formId: string, showOnlyOnce: boolean) => {
      klaviyoOpenForm(formId);

      if (showOnlyOnce) {
        markAsShown(formId);
      }
    },
    [markAsShown],
  );

  const localizedPathname = React.useMemo(
    () => (containsLocale(pathname) ? pathname : `/de${pathname}`),
    [pathname],
  );

  React.useEffect(() => {
    if (data && !formShown.current) {
      const match = data.klaviyoForms.find(
        (form) => form && formMatchesLocation(form, localizedPathname, search),
      );

      let timerId;

      if (match && !wasFormShown(match.formId)) {
        timerId = setTimeout(() => {
          triggerPopup(match.formId, match.showOnlyOnce);
        }, match.showAfter * 1000);

        // We need to use `useRef` instead useState because setting `formShown` triggers
        // cleanup phase of this `useEffect` and clears the timeout immediately when using
        // `useState`
        formShown.current = true;
      }
      return () => clearTimeout(timerId);
    }
  }, [
    data,
    localizedPathname,
    search,
    triggerPopup,
    wasFormShown,
    markAsShown,
  ]);
};
